import {
  Link,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useRouterState,
} from "@tanstack/react-router"
import type { RouterHistory } from "@tanstack/react-router"

import { FoundationLayout } from "@/components/app/foundation-layout"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  SignInPage,
} from "@/features/auth/sign-in-page"
import { FleetSafetySuperAdminSignInPage } from "@/features/auth/fleet-safety-super-admin-sign-in-page"
import {
  SignUpPage,
} from "@/features/auth/sign-up-page"
import { FleetInviteAcceptancePage } from "@/features/auth/fleet-invite-acceptance-page"
import { ClerkRouteGuard } from "@/features/auth/clerk-route-guard"
import { AccountHomePage } from "@/features/account/account-home-page"
import { AccountOnboardingPage } from "@/features/account/account-onboarding-page"
import { OperatorShellRoute } from "@/features/operator/shared/operator-shell-route"
import {
  AuthorityWorkspace,
  type AuthorityDeskSearch,
  type AuthorityReportsSearch,
} from "@/features/operator/authority/authority-workspace"
import {
  AdminStockWorkspace,
  type AdminStockOpsSearch,
} from "@/features/operator/admin/admin-stock-workspace"
import { AdminStockPrintPreview } from "@/features/operator/admin/admin-stock-print-preview"
import {
  AdminWorkspace,
  type AdminDashboardSearch,
} from "@/features/operator/admin/admin-workspace"
import {
  FleetWorkspace,
  type FleetAnalyticsSearch,
  type FleetCertificationsSearch,
  type FleetDashboardSearch,
} from "@/features/operator/fleet/fleet-workspace"
import { FleetAuthorityDirectoryPage } from "@/features/operator/fleet/fleet-authority-directory-page"
import { FleetEntryPage } from "@/features/operator/fleet/fleet-entry-page"
import { FleetOnboardingOrganizationPage } from "@/features/operator/fleet/fleet-onboarding-organization-page"
import { FleetOnboardingImportPage } from "@/features/operator/fleet/fleet-onboarding-import-page"
import { FleetOnboardingManualPage } from "@/features/operator/fleet/fleet-onboarding-manual-page"
import { FleetOnboardingReviewPage } from "@/features/operator/fleet/fleet-onboarding-review-page"
import { FleetOperatingUnitsPage } from "@/features/operator/fleet/fleet-operating-units-page"
import { FleetAssetsPage } from "@/features/operator/fleet/fleet-assets-page"
import { FleetPeoplePage } from "@/features/operator/fleet/fleet-people-page"
import { FleetAssignmentsPage } from "@/features/operator/fleet/fleet-assignments-page"
import { FleetKitsPage } from "@/features/operator/fleet/fleet-kits-page"
import { FleetKitsPrintPage } from "@/features/operator/fleet/fleet-kits-print-page"
import {
  FLEET_ONBOARDING_MODE_OPTIONS,
  FLEET_ONBOARDING_STEP_OPTIONS,
  FLEET_VERTICAL_OPTIONS,
  resolveFleetOnboardingMode,
  resolveFleetOnboardingVertical,
  type FleetOnboardingSearch,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OrchardWorkspace,
  type OrchardWorkspaceSearch,
} from "@/features/operator/orchards/orchard-workspace"
import {
  OpsMonitoringWorkspace,
  type OpsMonitoringSearch,
} from "@/features/operator/ops/ops-monitoring-workspace"
import {
  OpsIntegrationsWorkspace,
  type OpsIntegrationsSearch,
} from "@/features/operator/ops/ops-integrations-workspace"
import {
  OpsPartnerDeliveriesWorkspace,
  type OpsPartnerDeliveriesSearch,
} from "@/features/operator/ops/ops-partner-deliveries-workspace"
import {
  ExportsWorkspace,
  type ExportsSearch,
} from "@/features/operator/exports/exports-workspace"
import {
  ExportsFnolWorkspace,
  type ExportsFnolSearch,
} from "@/features/operator/exports/exports-fnol-workspace"
import { PublicGrievancePage } from "@/features/public/grievance/public-grievance-page"
import { LandingPage } from "@/features/public/landing/landing-page"
import { FleetSafetyLandingPage } from "@/features/public/fleet-safety/fleet-safety-landing-page"
import { PublicFleetPilotPage } from "@/features/public/fleet-safety/public-fleet-pilot-page"
import { PublicMyDevicePage } from "@/features/public/my-device/public-my-device-page"
import { OrchardFieldLayout } from "@/features/public/orchard/orchard-field-layout"
import { PublicOrchardTreePage } from "@/features/public/orchard/public-orchard-tree-page"
import { PrivacyPage } from "@/features/public/privacy/privacy-page"
import { PublicRegisterPage } from "@/features/public/register/public-register-page"
import { PublicReportPage } from "@/features/public/report/public-report-page"
import { PublicIncidentEscalationPacketPage } from "@/features/public/incident-escalation/public-incident-escalation-packet-page"
import { TermsPage } from "@/features/public/terms/terms-page"
import { resolveScanTag, type PublicScanSearch } from "@/features/public/shared/scan"
import { isSafetyQrKitVariant } from "@/lib/safetyqr-catalog"
import { publicShellLinks } from "@/lib/surfaces"

type ClerkAuthSearch = Partial<Record<`__clerk_${string}`, string>>

type AuthEntrySearch = ClerkAuthSearch & {
  fleet_invite?: "1"
  redirect_url?: string
}

const FLEET_COMPANY_DETAILS_ONBOARDING_PATH = "/fleet/onboarding/organization"

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "privacy",
  component: PrivacyPage,
})

const fleetSafetyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-safety",
  component: FleetSafetyRoutePage,
})

const fleetSafetyPilotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-safety/pilot",
  component: FleetSafetyPilotRoutePage,
})

const fleetSafetySuperAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-safety/super-admin",
  validateSearch: validateAuthEntrySearch,
  component: FleetSafetySuperAdminRoutePage,
})

const fleetSafetySuperAdminCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-safety/super-admin/$",
  validateSearch: validateAuthEntrySearch,
  component: FleetSafetySuperAdminRoutePage,
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "terms",
  component: TermsPage,
})

const grievanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "grievance",
  component: GrievanceRoutePage,
})

const reportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "report",
  validateSearch: validatePublicScanSearch,
  component: ReportRoutePage,
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "register",
  validateSearch: validatePublicScanSearch,
  component: RegisterRoutePage,
})

const myDeviceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "my-device",
  validateSearch: validatePublicScanSearch,
  component: MyDeviceRoutePage,
})

const orchardTreeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "orchard/tree/$token",
  component: OrchardTreeRoutePage,
})

const incidentEscalationPacketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "iep/$token",
  component: IncidentEscalationPacketRoutePage,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  validateSearch: validateAuthEntrySearch,
  component: SignInRoutePage,
})

const signInCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in/$",
  validateSearch: validateAuthEntrySearch,
  component: SignInRoutePage,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up",
  validateSearch: validateAuthEntrySearch,
  component: SignUpRoutePage,
})

const signUpCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up/$",
  validateSearch: validateAuthEntrySearch,
  component: SignUpRoutePage,
})

const fleetInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-invite",
  validateSearch: validateAuthEntrySearch,
  component: FleetInviteRoutePage,
})

const fleetInviteCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "fleet-invite/$",
  validateSearch: validateAuthEntrySearch,
  component: FleetInviteRoutePage,
})

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account",
  component: AccountRoutePage,
})

const accountOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "account/onboarding",
  component: AccountOnboardingRoutePage,
})

const operatorShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "operator-shell",
  component: OperatorShellRoute,
})

const authorityRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "authority",
  validateSearch: validateAuthorityDeskSearch,
  component: AuthorityDeskRoutePage,
})

const authorityIncidentRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "authority/incidents/$incidentId",
  validateSearch: validateAuthorityDeskSearch,
  component: AuthorityDeskIncidentRoutePage,
})

const authorityReportsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "authority/reports",
  validateSearch: validateAuthorityReportsSearch,
  component: AuthorityReportsRoutePage,
})

const authorityReportsIncidentRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "authority/reports/incidents/$incidentId",
  validateSearch: validateAuthorityReportsSearch,
  component: AuthorityReportsIncidentRoutePage,
})

const fleetRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetDashboardRoutePage,
})

const fleetIncidentsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/incidents",
  validateSearch: validateFleetDashboardSearch,
  component: FleetIncidentsRoutePage,
})

const fleetAuthorityDirectoryRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/authority-directory",
  component: FleetAuthorityDirectoryRoutePage,
})

const fleetIncidentRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/incidents/$incidentId",
  validateSearch: validateFleetDashboardSearch,
  component: FleetDashboardIncidentRoutePage,
})

const fleetAnalyticsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/analytics",
  validateSearch: validateFleetAnalyticsSearch,
  component: FleetAnalyticsRoutePage,
})

const fleetCertificationsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/certifications",
  validateSearch: validateFleetCertificationsSearch,
  component: FleetCertificationsRoutePage,
})

const fleetOnboardingRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/onboarding",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOnboardingRoutePage,
})

const fleetOnboardingOrganizationRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/onboarding/organization",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOnboardingOrganizationRoutePage,
})

const fleetOnboardingImportRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/onboarding/import",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOnboardingImportRoutePage,
})

const fleetOnboardingManualRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/onboarding/manual",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOnboardingManualRoutePage,
})

const fleetOnboardingReviewRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/onboarding/review",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOnboardingReviewRoutePage,
})

const fleetOperatingUnitsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/operating-units",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetOperatingUnitsRoutePage,
})

const fleetAssetsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/assets",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetAssetsRoutePage,
})

const fleetPeopleRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/people",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetPeopleRoutePage,
})

const fleetAssignmentsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/assignments",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetAssignmentsRoutePage,
})

const fleetKitsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/kits",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetKitsRoutePage,
})

const fleetKitsPrintRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/kits/print",
  validateSearch: validateFleetOnboardingSearch,
  component: FleetKitsPrintRoutePage,
})

const fleetTagsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "fleet/tags",
  validateSearch: validateFleetDashboardSearch,
  component: FleetTagsRoutePage,
})

const orchardsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "orchards",
  validateSearch: validateOrchardWorkspaceSearch,
  component: OrchardsRoutePage,
})

const adminRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "admin",
  validateSearch: validateAdminDashboardSearch,
  component: AdminRoutePage,
})

const adminStockOpsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "admin/stock-ops",
  validateSearch: validateAdminStockOpsSearch,
  component: AdminStockOpsRoutePage,
})

const adminStockPrintRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "admin/stock-ops/print",
  validateSearch: validateAdminStockPrintSearch,
  component: AdminStockPrintRoutePage,
})

const opsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "ops",
  validateSearch: validateOpsMonitoringSearch,
  component: OpsRoutePage,
})

const opsIntegrationsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "ops/integrations",
  validateSearch: validateOpsIntegrationsSearch,
  component: OpsIntegrationsRoutePage,
})

const opsPartnerDeliveriesRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "ops/partner-deliveries",
  validateSearch: validateOpsPartnerDeliveriesSearch,
  component: OpsPartnerDeliveriesRoutePage,
})

const exportsRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "exports",
  validateSearch: validateExportsSearch,
  component: ExportsRoutePage,
})

const exportsFnolRoute = createRoute({
  getParentRoute: () => operatorShellRoute,
  path: "exports/fnol",
  validateSearch: validateExportsFnolSearch,
  component: ExportsFnolRoutePage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  fleetSafetyRoute,
  fleetSafetyPilotRoute,
  fleetSafetySuperAdminRoute,
  fleetSafetySuperAdminCatchAllRoute,
  privacyRoute,
  termsRoute,
  grievanceRoute,
  reportRoute,
  registerRoute,
  myDeviceRoute,
  orchardTreeRoute,
  incidentEscalationPacketRoute,
  signInRoute,
  signInCatchAllRoute,
  signUpRoute,
  signUpCatchAllRoute,
  fleetInviteRoute,
  fleetInviteCatchAllRoute,
  accountRoute,
  accountOnboardingRoute,
  operatorShellRoute.addChildren([
    authorityRoute,
    authorityIncidentRoute,
    authorityReportsRoute,
    authorityReportsIncidentRoute,
    fleetRoute,
    fleetIncidentsRoute,
    fleetAuthorityDirectoryRoute,
    fleetIncidentRoute,
    fleetAnalyticsRoute,
    fleetCertificationsRoute,
    fleetOnboardingRoute,
    fleetOnboardingOrganizationRoute,
    fleetOnboardingImportRoute,
    fleetOnboardingManualRoute,
    fleetOnboardingReviewRoute,
    fleetOperatingUnitsRoute,
    fleetAssetsRoute,
    fleetPeopleRoute,
    fleetAssignmentsRoute,
    fleetKitsRoute,
    fleetKitsPrintRoute,
    fleetTagsRoute,
    orchardsRoute,
    adminRoute,
    adminStockOpsRoute,
    adminStockPrintRoute,
    opsRoute,
    opsIntegrationsRoute,
    opsPartnerDeliveriesRoute,
    exportsRoute,
    exportsFnolRoute,
  ]),
])

export function createAppRouter(options?: { history?: RouterHistory }) {
  return createRouter({
    routeTree,
    history: options?.history,
    defaultPreload: "intent",
    defaultStructuralSharing: true,
  })
}

export const router = createAppRouter()

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

function RootLayout() {
  return <Outlet />
}

function validatePublicScanSearch(search: Record<string, unknown>): PublicScanSearch {
  return {
    card: normalizeSearchValue(search.card),
    code: normalizeSearchValue(search.code),
    device: normalizeSearchValue(search.device),
    qr: normalizeSearchValue(search.qr),
    tag: normalizeSearchValue(search.tag),
    vehicle: normalizeSearchValue(search.vehicle),
  }
}

function validateAuthEntrySearch(search: Record<string, unknown>): AuthEntrySearch {
  return {
    ...extractClerkAuthSearch(search),
    fleet_invite: normalizeSearchValue(search.fleet_invite) === "1" ? "1" : undefined,
    redirect_url: normalizeSearchValue(search.redirect_url),
  }
}

function extractClerkAuthSearch(search: Record<string, unknown>): ClerkAuthSearch {
  const next: ClerkAuthSearch = {}

  for (const [key, value] of Object.entries(search)) {
    if (!key.startsWith("__clerk_")) {
      continue
    }

    const normalizedValue = normalizeSearchValue(value)
    if (!normalizedValue) {
      continue
    }

    next[key as `__clerk_${string}`] = normalizedValue
  }

  return next
}

function normalizeSearchValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function normalizeEnumSearchValue<TValue extends string>(
  value: unknown,
  allowedValues: readonly TValue[]
) {
  const normalized = normalizeSearchValue(value)

  if (!normalized) {
    return undefined
  }

  return allowedValues.includes(normalized as TValue)
    ? (normalized as TValue)
    : undefined
}

function normalizeAdminStockKitVariantSearchValue(value: unknown) {
  const normalized = normalizeSearchValue(value)

  if (!normalized) {
    return undefined
  }

  if (normalized === "all") {
    return "all" as const
  }

  return isSafetyQrKitVariant(normalized) ? normalized : undefined
}

function normalizePositiveIncidentId(value: string | undefined) {
  if (!value) {
    return undefined
  }

  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function mergeOptionalSearch<TSearch extends Record<string, unknown>>(
  previous: TSearch,
  next: Partial<TSearch>
): TSearch {
  return Object.fromEntries(
    Object.entries({
      ...previous,
      ...next,
    }).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ) as TSearch
}

function validateAuthorityDeskSearch(search: Record<string, unknown>): AuthorityDeskSearch {
  return {
    incidentStatus: normalizeEnumSearchValue(search.incidentStatus, [
      "all",
      "reported",
      "triaged",
      "dispatched",
      "active",
      "resolved",
      "closed",
    ] as const),
    jurisdictionId: normalizeSearchValue(search.jurisdictionId),
    q: normalizeSearchValue(search.q),
    severity: normalizeEnumSearchValue(search.severity, [
      "all",
      "critical",
      "severe",
      "moderate",
      "minor",
    ] as const),
  }
}

function validateAuthorityReportsSearch(
  search: Record<string, unknown>
): AuthorityReportsSearch {
  return {
    incidentStatus: normalizeEnumSearchValue(search.incidentStatus, [
      "all",
      "reported",
      "triaged",
      "dispatched",
      "active",
      "resolved",
      "closed",
    ] as const),
    q: normalizeSearchValue(search.q),
    reportKind: normalizeEnumSearchValue(search.reportKind, [
      "all",
      "incident",
      "service_request",
      "behavior",
    ] as const),
  }
}

function validateFleetDashboardSearch(
  search: Record<string, unknown>
): FleetDashboardSearch {
  return {
    assignmentStatus: normalizeSearchValue(search.assignmentStatus),
    incidentQuery: normalizeSearchValue(search.incidentQuery),
    incidentStatus: normalizeEnumSearchValue(search.incidentStatus, [
      "all",
      "pending",
      "in_progress",
      "resolved",
    ] as const),
    memberType: normalizeSearchValue(search.memberType),
    tagQuery: normalizeSearchValue(search.tagQuery),
    tagStatus: normalizeSearchValue(search.tagStatus),
  }
}

function validateFleetAnalyticsSearch(
  search: Record<string, unknown>
): FleetAnalyticsSearch {
  return {
    days: normalizeEnumSearchValue(search.days, ["7", "30", "90"] as const),
  }
}

function validateFleetCertificationsSearch(
  search: Record<string, unknown>
): FleetCertificationsSearch {
  return {
    program: normalizeSearchValue(search.program),
    q: normalizeSearchValue(search.q),
    sort: normalizeEnumSearchValue(search.sort, [
      "artifact",
      "expiring",
      "score",
    ] as const),
    status: normalizeSearchValue(search.status),
  }
}

function validateFleetOnboardingSearch(
  search: Record<string, unknown>
): FleetOnboardingSearch {
  return {
    batchCode: normalizeSearchValue(search.batchCode),
    mode: normalizeEnumSearchValue(search.mode, FLEET_ONBOARDING_MODE_OPTIONS),
    organizationId: normalizeSearchValue(search.organizationId),
    runId: normalizeSearchValue(search.runId),
    step: normalizeEnumSearchValue(search.step, FLEET_ONBOARDING_STEP_OPTIONS),
    vertical: normalizeEnumSearchValue(search.vertical, FLEET_VERTICAL_OPTIONS),
  }
}

function validateAdminDashboardSearch(
  search: Record<string, unknown>
): AdminDashboardSearch {
  return {
    organizationId: normalizeSearchValue(search.organizationId),
    section: normalizeEnumSearchValue(search.section, [
      "overview",
      "cards",
      "organizations",
    ] as const),
  }
}

function validateOrchardWorkspaceSearch(
  search: Record<string, unknown>
): OrchardWorkspaceSearch {
  return {
    analyticsFrom: normalizeSearchValue(search.analyticsFrom),
    analyticsTo: normalizeSearchValue(search.analyticsTo),
    orchardId: normalizeSearchValue(search.orchardId),
    organizationId: normalizeSearchValue(search.organizationId),
    section: normalizeEnumSearchValue(search.section, [
      "overview",
      "orchards",
      "trees",
      "qr_export",
    ] as const),
  }
}

function validateAdminStockOpsSearch(
  search: Record<string, unknown>
): AdminStockOpsSearch {
  return {
    kitVariant: normalizeAdminStockKitVariantSearchValue(search.kitVariant),
    lifecycleState: normalizeEnumSearchValue(search.lifecycleState, [
      "all",
      "generated",
      "printed",
      "distributed",
    ] as const),
  }
}

function validateAdminStockPrintSearch(search: Record<string, unknown>) {
  return {
    autoPrint:
      typeof search.autoPrint === "string"
        ? search.autoPrint === "true"
        : undefined,
    batchId: normalizeSearchValue(search.batchId),
    returnTo: normalizeSearchValue(search.returnTo),
  }
}

function validateOpsMonitoringSearch(
  search: Record<string, unknown>
): OpsMonitoringSearch {
  return {
    organizationId: normalizeSearchValue(search.organizationId),
    pilotLeadId: normalizeSearchValue(search.pilotLeadId),
  }
}

function validateOpsIntegrationsSearch(
  search: Record<string, unknown>
): OpsIntegrationsSearch {
  return {
    deliveryId: normalizeSearchValue(search.deliveryId),
    latestStatus: normalizeEnumSearchValue(search.latestStatus, [
      "all",
      "prepared",
      "scheduled",
      "queued",
      "processing",
      "submitted",
      "acknowledged",
      "retryable",
      "delivered",
      "failed",
      "dead_letter",
    ] as const),
    organizationId: normalizeSearchValue(search.organizationId),
    partner: normalizeSearchValue(search.partner),
    posture: normalizeEnumSearchValue(search.posture, [
      "all",
      "dead_letter",
      "delivered",
      "in_flight",
      "replayed",
      "retry_queue",
    ] as const),
    q: normalizeSearchValue(search.q),
  }
}

function validateOpsPartnerDeliveriesSearch(
  search: Record<string, unknown>
): OpsPartnerDeliveriesSearch {
  return {
    deliveryId: normalizeSearchValue(search.deliveryId),
    deliveryStatus: normalizeEnumSearchValue(search.deliveryStatus, [
      "all",
      "active",
      "dead_letter",
      "delivered",
      "not_started",
      "retryable",
    ] as const),
    exportId: normalizeSearchValue(search.exportId),
    exportType: normalizeEnumSearchValue(search.exportType, [
      "all",
      "evidence",
      "government_share",
      "institutional_summary",
      "insurance_fnol",
    ] as const),
    organizationId: normalizeSearchValue(search.organizationId),
    q: normalizeSearchValue(search.q),
  }
}

function validateExportsSearch(search: Record<string, unknown>): ExportsSearch {
  return {
    exportId: normalizeSearchValue(search.exportId),
    exportType: normalizeEnumSearchValue(search.exportType, [
      "all",
      "evidence",
      "government_share",
      "institutional_summary",
      "insurance_fnol",
    ] as const),
    organizationId: normalizeSearchValue(search.organizationId),
    q: normalizeSearchValue(search.q),
  }
}

function validateExportsFnolSearch(
  search: Record<string, unknown>
): ExportsFnolSearch {
  return {
    exportType: normalizeEnumSearchValue(search.exportType, [
      "all",
      "evidence",
      "government_share",
      "institutional_summary",
      "insurance_fnol",
    ] as const),
    incidentId: normalizeSearchValue(search.incidentId),
    q: normalizeSearchValue(search.q),
    readiness: normalizeEnumSearchValue(search.readiness, [
      "all",
      "missing_required_fields",
      "ready",
      "submitted",
    ] as const),
  }
}

function PublicShellLayout(props: { children: React.ReactNode; showLinks?: boolean }) {
  return (
    <FoundationLayout
      title="SafetyQR"
      description="Report incidents, register devices, and manage your safety information."
      links={publicShellLinks}
      showLinks={props.showLinks}
    >
      {props.children}
    </FoundationLayout>
  )
}

function FleetSafetyRoutePage() {
  return <FleetSafetyLandingPage />
}

function FleetSafetyPilotRoutePage() {
  return (
    <PublicShellLayout>
      <PublicFleetPilotPage />
    </PublicShellLayout>
  )
}

function FleetSafetySuperAdminRoutePage() {
  const redirectUrl = useRouterState({
    select: (state) => normalizeSearchValue(state.location.search.redirect_url),
  })

  return (
    <FleetSafetySuperAdminSignInPage
      path="/fleet-safety/super-admin"
      redirectUrl={redirectUrl}
    />
  )
}


function ReportRoutePage() {
  const search = reportRoute.useSearch()
  const scanTag = resolveScanTag(search)

  return (
    <PublicShellLayout showLinks={!scanTag}>
      <PublicReportPage initialSearch={search} initialTag={scanTag} />
    </PublicShellLayout>
  )
}

function RegisterRoutePage() {
  const search = registerRoute.useSearch()
  const scanTag = resolveScanTag(search)

  return (
    <PublicShellLayout showLinks={!scanTag}>
      <PublicRegisterPage initialSearch={search} initialTag={scanTag} />
    </PublicShellLayout>
  )
}

function GrievanceRoutePage() {
  const searchParams =
    typeof window === "undefined"
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search)

  return (
    <PublicShellLayout>
      <PublicGrievancePage
        initialCaseType={searchParams.get("caseType")}
        initialReason={searchParams.get("reason")}
        initialSourceSurface={searchParams.get("source")}
        initialTag={searchParams.get("tag")}
      />
    </PublicShellLayout>
  )
}

function MyDeviceRoutePage() {
  const search = myDeviceRoute.useSearch()
  const scanTag = resolveScanTag(search)

  return (
    <PublicShellLayout showLinks={!scanTag}>
      <PublicMyDevicePage initialTag={scanTag} />
    </PublicShellLayout>
  )
}

function OrchardTreeRoutePage() {
  const { token } = orchardTreeRoute.useParams()

  return (
    <OrchardFieldLayout>
      <PublicOrchardTreePage initialToken={token} />
    </OrchardFieldLayout>
  )
}

function IncidentEscalationPacketRoutePage() {
  const { token } = incidentEscalationPacketRoute.useParams()

  return <PublicIncidentEscalationPacketPage initialToken={token} />
}

function SignInRoutePage() {
  const authEntrySearch = useRouterState({
    select: (state) => state.location.search as AuthEntrySearch,
  })
  const redirectUrl = useRouterState({
    select: (state) => normalizeSearchValue(state.location.search.redirect_url),
  })
  const isFleetInvite = authEntrySearch.fleet_invite === "1"

  return (
    <SignInPage
      redirectUrl={redirectUrl}
      variant={isFleetInvite ? "fleetClient" : "default"}
    />
  )
}

function SignUpRoutePage() {
  const redirectUrl = useRouterState({
    select: (state) => normalizeSearchValue(state.location.search.redirect_url),
  })

  return <SignUpPage redirectUrl={redirectUrl} />
}

function FleetInviteRoutePage() {
  const authEntrySearch = useRouterState({
    select: (state) => state.location.search as AuthEntrySearch,
  })
  const redirectUrl = useRouterState({
    select: (state) => normalizeSearchValue(state.location.search.redirect_url),
  })

  return (
    <FleetInviteAcceptancePage
      redirectUrl={redirectUrl ?? FLEET_COMPANY_DETAILS_ONBOARDING_PATH}
      status={authEntrySearch.__clerk_status}
      ticket={authEntrySearch.__clerk_ticket}
    />
  )
}

function AccountRoutePage() {
  return (
    <ClerkRouteGuard title="Loading your account">
      <AccountHomePage />
    </ClerkRouteGuard>
  )
}

function AccountOnboardingRoutePage() {
  return (
    <ClerkRouteGuard title="Loading orchard setup">
      <AccountOnboardingPage />
    </ClerkRouteGuard>
  )
}

function AuthorityDeskRoutePage() {
  const search = authorityRoute.useSearch()
  const navigate = authorityRoute.useNavigate()

  return (
    <AuthorityWorkspace
      mode="desk"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(incidentId) => {
        void navigate({
          to: "/authority/incidents/$incidentId",
          params: {
            incidentId: String(incidentId),
          },
          search,
        })
      }}
    />
  )
}

function AuthorityDeskIncidentRoutePage() {
  const { incidentId } = authorityIncidentRoute.useParams()
  const search = authorityIncidentRoute.useSearch()
  const navigate = authorityIncidentRoute.useNavigate()

  return (
    <AuthorityWorkspace
      incidentId={normalizePositiveIncidentId(incidentId)}
      mode="desk"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(nextIncidentId) => {
        void navigate({
          to: "/authority/incidents/$incidentId",
          params: {
            incidentId: String(nextIncidentId),
          },
          search,
        })
      }}
    />
  )
}

function AuthorityReportsRoutePage() {
  const search = authorityReportsRoute.useSearch()
  const navigate = authorityReportsRoute.useNavigate()

  return (
    <AuthorityWorkspace
      mode="reports"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(incidentId) => {
        void navigate({
          to: "/authority/reports/incidents/$incidentId",
          params: {
            incidentId: String(incidentId),
          },
          search,
        })
      }}
    />
  )
}

function AuthorityReportsIncidentRoutePage() {
  const { incidentId } = authorityReportsIncidentRoute.useParams()
  const search = authorityReportsIncidentRoute.useSearch()
  const navigate = authorityReportsIncidentRoute.useNavigate()

  return (
    <AuthorityWorkspace
      incidentId={normalizePositiveIncidentId(incidentId)}
      mode="reports"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(nextIncidentId) => {
        void navigate({
          to: "/authority/reports/incidents/$incidentId",
          params: {
            incidentId: String(nextIncidentId),
          },
          search,
        })
      }}
    />
  )
}

function FleetDashboardRoutePage() {
  const search = fleetRoute.useSearch()
  const navigate = fleetRoute.useNavigate()

  return (
    <FleetEntryPage
      search={search}
      onOpenRun={(input) => {
        const resolvedStep = input.step === "overview" ? "organization" : input.step
        const destination =
          resolvedStep === "import"
            ? "/fleet/onboarding/import"
            : resolvedStep === "manual"
              ? "/fleet/onboarding/manual"
              : resolvedStep === "review"
                ? "/fleet/onboarding/review"
                : "/fleet/onboarding/organization"

        void navigate({
          to: destination,
          search: mergeOptionalSearch(search, {
            mode: input.mode,
            organizationId: input.organizationId,
            runId: input.runId,
            step: resolvedStep,
            vertical: input.vertical,
          }),
        })
      }}
      onOpenSetup={(input) => {
        void navigate({
          to: "/fleet/onboarding/organization",
          search: mergeOptionalSearch(search, {
            mode: input.mode,
            organizationId: input.organizationId,
            runId: input.runId,
            step: "organization",
            vertical: input.vertical,
          }),
        })
      }}
    />
  )
}

function FleetDashboardIncidentRoutePage() {
  const { incidentId } = fleetIncidentRoute.useParams()
  const search = fleetIncidentRoute.useSearch()
  const navigate = fleetIncidentRoute.useNavigate()

  return (
    <FleetWorkspace
      incidentId={normalizePositiveIncidentId(incidentId)}
      mode="dashboard"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(nextIncidentId) => {
        void navigate({
          to: "/fleet/incidents/$incidentId",
          params: {
            incidentId: String(nextIncidentId),
          },
          search,
        })
      }}
    />
  )
}

function FleetIncidentsRoutePage() {
  const search = fleetIncidentsRoute.useSearch()
  const navigate = fleetIncidentsRoute.useNavigate()

  return (
    <FleetWorkspace
      mode="dashboard"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={(nextIncidentId) => {
        void navigate({
          to: "/fleet/incidents/$incidentId",
          params: {
            incidentId: String(nextIncidentId),
          },
          search,
        })
      }}
    />
  )
}

function FleetAuthorityDirectoryRoutePage() {
  return <FleetAuthorityDirectoryPage />
}

function FleetAnalyticsRoutePage() {
  const search = fleetAnalyticsRoute.useSearch()
  const navigate = fleetAnalyticsRoute.useNavigate()

  return (
    <FleetWorkspace
      mode="analytics"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={() => {}}
    />
  )
}

function FleetCertificationsRoutePage() {
  const search = fleetCertificationsRoute.useSearch()
  const navigate = fleetCertificationsRoute.useNavigate()

  return (
    <FleetWorkspace
      mode="certifications"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={() => {}}
    />
  )
}

function FleetOnboardingRoutePage() {
  const search = fleetOnboardingRoute.useSearch()
  const navigate = fleetOnboardingRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetOnboardingOrganizationPage
      search={search}
      onContinue={(selection) => {
        void navigate({
          to: selection.mode === "manual" ? "/fleet/onboarding/manual" : "/fleet/onboarding/import",
          search: mergeOptionalSearch(search, {
            mode: selection.mode ?? selectedMode,
            organizationId: search.organizationId ?? draft.activeOrganizationId ?? undefined,
            runId: search.runId ?? draft.activeRunId ?? undefined,
            step: selection.mode === "manual" ? "manual" : "import",
            vertical: selection.vertical ?? selectedVertical,
          }),
        })
      }}
    />
  )
}

function FleetOnboardingOrganizationRoutePage() {
  const search = fleetOnboardingOrganizationRoute.useSearch()
  const navigate = fleetOnboardingOrganizationRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetOnboardingOrganizationPage
      search={search}
      onContinue={(selection) => {
        void navigate({
          to: selection.mode === "manual" ? "/fleet/onboarding/manual" : "/fleet/onboarding/import",
          search: mergeOptionalSearch(search, {
            mode: selection.mode ?? selectedMode,
            organizationId: search.organizationId ?? draft.activeOrganizationId ?? undefined,
            runId: search.runId ?? draft.activeRunId ?? undefined,
            step: selection.mode === "manual" ? "manual" : "import",
            vertical: selection.vertical ?? selectedVertical,
          }),
        })
      }}
    />
  )
}

function FleetOnboardingImportRoutePage() {
  const search = fleetOnboardingImportRoute.useSearch()
  const navigate = fleetOnboardingImportRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetOnboardingImportPage
      search={search}
      onContinueToReview={() => {
        void navigate({
          to: "/fleet/onboarding/review",
          search: mergeOptionalSearch(search, {
            mode: "import",
            step: "review",
            vertical: selectedVertical,
          }),
        })
      }}
    />
  )
}

function FleetOnboardingManualRoutePage() {
  const search = fleetOnboardingManualRoute.useSearch()
  const navigate = fleetOnboardingManualRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetOnboardingManualPage
      search={search}
      onContinueToReview={() => {
        void navigate({
          to: "/fleet/onboarding/review",
          search: mergeOptionalSearch(search, {
            mode: "manual",
            step: "review",
            vertical: selectedVertical,
          }),
        })
      }}
    />
  )
}

function FleetOnboardingReviewRoutePage() {
  const search = fleetOnboardingReviewRoute.useSearch()
  const navigate = fleetOnboardingReviewRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetOnboardingReviewPage
      search={search}
      onReturnToMode={() => {
        void navigate({
          to: selectedMode === "manual" ? "/fleet/onboarding/manual" : "/fleet/onboarding/import",
          search: mergeOptionalSearch(search, {
            mode: selectedMode,
            step: selectedMode === "manual" ? "manual" : "import",
            vertical: selectedVertical,
          }),
        })
      }}
      onContinueToKits={(context) => {
        void navigate({
          to: "/fleet/kits",
          search: mergeOptionalSearch(search, {
            mode: context.mode,
            organizationId: context.organizationId,
            runId: context.runId ?? undefined,
            vertical: context.vertical,
          }),
        })
      }}
    />
  )
}

function FleetOperatingUnitsRoutePage() {
  const search = fleetOperatingUnitsRoute.useSearch()
  return <FleetOperatingUnitsPage search={search} />
}

function FleetAssetsRoutePage() {
  const search = fleetAssetsRoute.useSearch()
  return <FleetAssetsPage search={search} />
}

function FleetPeopleRoutePage() {
  const search = fleetPeopleRoute.useSearch()
  return <FleetPeoplePage search={search} />
}

function FleetAssignmentsRoutePage() {
  const search = fleetAssignmentsRoute.useSearch()
  return <FleetAssignmentsPage search={search} />
}

function FleetKitsRoutePage() {
  const search = fleetKitsRoute.useSearch()
  const navigate = fleetKitsRoute.useNavigate()
  const { draft } = useFleetOnboardingDraft()
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)

  return (
    <FleetKitsPage
      search={search}
      onOpenPrintPrep={(batchCode) => {
        void navigate({
          to: "/fleet/kits/print",
          search: mergeOptionalSearch(search, {
            batchCode,
            mode: selectedMode,
            organizationId: search.organizationId ?? draft.masterPersistence?.organizationId,
            vertical: selectedVertical,
          }),
        })
      }}
      onReturnToReview={() => {
        void navigate({
          to: "/fleet/onboarding/review",
          search: mergeOptionalSearch(search, {
            mode: selectedMode,
            step: "review",
            vertical: selectedVertical,
          }),
        })
      }}
    />
  )
}

function FleetKitsPrintRoutePage() {
  const search = fleetKitsPrintRoute.useSearch()
  const navigate = fleetKitsPrintRoute.useNavigate()

  return (
    <FleetKitsPrintPage
      search={search}
      onBackToKits={() => {
        void navigate({
          to: "/fleet/kits",
          search,
        })
      }}
    />
  )
}

function FleetTagsRoutePage() {
  const search = fleetTagsRoute.useSearch()
  const navigate = fleetTagsRoute.useNavigate()

  return (
    <FleetWorkspace
      mode="tags"
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
      onSelectIncident={() => {}}
    />
  )
}

function OrchardsRoutePage() {
  const search = orchardsRoute.useSearch()
  const navigate = orchardsRoute.useNavigate()

  return (
    <OrchardWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function AdminRoutePage() {
  const search = adminRoute.useSearch()
  const navigate = adminRoute.useNavigate()

  return (
    <AdminWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function AdminStockOpsRoutePage() {
  const search = adminStockOpsRoute.useSearch()
  const navigate = adminStockOpsRoute.useNavigate()

  return (
    <AdminStockWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function AdminStockPrintRoutePage() {
  const search = adminStockPrintRoute.useSearch()

  return <AdminStockPrintPreview search={search} />
}

function OpsRoutePage() {
  const search = opsRoute.useSearch()
  const navigate = opsRoute.useNavigate()

  return (
    <OpsMonitoringWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          to: "/ops",
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function OpsIntegrationsRoutePage() {
  const search = opsIntegrationsRoute.useSearch()
  const navigate = opsIntegrationsRoute.useNavigate()

  return (
    <OpsIntegrationsWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function OpsPartnerDeliveriesRoutePage() {
  const search = opsPartnerDeliveriesRoute.useSearch()
  const navigate = opsPartnerDeliveriesRoute.useNavigate()

  return (
    <OpsPartnerDeliveriesWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function ExportsRoutePage() {
  const search = exportsRoute.useSearch()
  const navigate = exportsRoute.useNavigate()

  return (
    <ExportsWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function ExportsFnolRoutePage() {
  const search = exportsFnolRoute.useSearch()
  const navigate = exportsFnolRoute.useNavigate()

  return (
    <ExportsFnolWorkspace
      search={search}
      onSearchChange={(next) => {
        void navigate({
          search: (previous) => mergeOptionalSearch(previous, next),
        })
      }}
    />
  )
}

function NotFoundPage() {
  return (
    <div className="min-h-svh px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl pt-16">
        <Card className="rounded-4xl border-border/70">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">
              Page not found
            </CardTitle>
            <CardDescription>
              The page you are looking for does not exist or has been moved.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild variant="outline">
              <Link to="/">Go to homepage</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
